import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  useComparisonMode,
  useEntityNames,
  useGroupedMeasures,
  useSelectedCapacityTeams,
  useSelectedInitiativeStatuses,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { exportCapacityCSV, transformCapacityData } from '../main-section/capacity/table/table.helpers';
import { exportInitiativeOverviewCSV } from '../main-section/initiative-overview/table/table.helpers';
import { useInitiativeOverviewTableData } from '../main-section/initiative-overview/table/table.hooks';
import {
  exportMonthlyAveragesCSV,
  transformMonthlyAveragesData,
} from '../main-section/monthly-averages/table/table.helpers';
import { ComparisonMode, MultipleViewMode, Tab, ViewMode } from '../process-analysis.type';
import { useCapacityData } from './capacity';
import { useMonthlyAveragesData } from './monthly-averages';

/**
 * Hook that provides capacity CSV export functionality
 *
 * @returns {(() => void) | undefined} CSV export handler function or undefined if CSV export is not available
 */
const useCapacityCSVExport = (): (() => void) | undefined => {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const comparisonMode = useComparisonMode();
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedCapacityTeams = useSelectedCapacityTeams();
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);

  // Only fetch capacity data when in capacity view mode
  const shouldFetchCapacityData =
    (comparisonMode === ComparisonMode.Single && viewMode === ViewMode.Capacity && activeTab === Tab.Portfolios) ||
    (comparisonMode === ComparisonMode.Multiple &&
      multipleViewMode === MultipleViewMode.Capacity &&
      activeTab === Tab.Teams);

  const { portfolioData, projectData } = useCapacityData(shouldFetchCapacityData);

  const projectIdToName = portfolio?.projects.map((project) => ({ id: project.id, name: project.name }));

  const shouldShowCapacityCSVExport =
    (comparisonMode === ComparisonMode.Single && viewMode === ViewMode.Capacity && activeTab === Tab.Portfolios) ||
    (comparisonMode === ComparisonMode.Multiple &&
      multipleViewMode === MultipleViewMode.Capacity &&
      activeTab === Tab.Teams);

  const handleCapacityCSVExport = () => {
    if (!projectData || Object.keys(projectData).length === 0) return;

    const selection = activeTab === Tab.Portfolios ? selectedCapacityTeams : selectedTeams;
    const tableData = transformCapacityData(projectData, portfolioData, selection, projectIdToName);

    if (tableData.length === 0) return;

    exportCapacityCSV(tableData, t);
  };

  return shouldShowCapacityCSVExport ? handleCapacityCSVExport : undefined;
};

/**
 * Hook that provides monthly averages CSV export functionality
 *
 * @returns {(() => void) | undefined} CSV export handler function or undefined if CSV export is not available
 */
const useMonthlyAveragesCSVExport = (): (() => void) | undefined => {
  const { t } = useTranslation();
  const comparisonMode = useComparisonMode();
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);

  // Only fetch monthly averages data when in monthly averages view mode
  const shouldFetchMonthlyAveragesData =
    comparisonMode === ComparisonMode.Multiple && multipleViewMode === MultipleViewMode.MonthlyAverages;

  const { data } = useMonthlyAveragesData(shouldFetchMonthlyAveragesData);
  const groupedMeasures = useGroupedMeasures();
  const { entityIds, entityNames } = useEntityNames();

  const shouldShowCSVExport =
    comparisonMode === ComparisonMode.Multiple && multipleViewMode === MultipleViewMode.MonthlyAverages;

  const handleMonthlyAveragesCSVExport = () => {
    if (!data || Object.keys(data).length === 0) return;

    const transformedData = transformMonthlyAveragesData(data, entityIds, groupedMeasures);
    exportMonthlyAveragesCSV(transformedData, entityIds, entityNames, t);
  };

  return shouldShowCSVExport ? handleMonthlyAveragesCSVExport : undefined;
};

/**
 * Hook that provides initiative overview CSV export functionality
 *
 * @returns {(() => void) | undefined} CSV export handler function or undefined if CSV export is not available
 */
const useInitiativeOverviewCSVExport = (): (() => void) | undefined => {
  const { t } = useTranslation();
  const comparisonMode = useComparisonMode();
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const initiatives = useProcessAnalysisStore((state) => state.initiatives);
  const selectedInitiativeStatuses = useSelectedInitiativeStatuses();

  const tableData = useInitiativeOverviewTableData(initiatives, selectedInitiativeStatuses);

  const shouldShowInitiativeOverviewCSVExport =
    comparisonMode === ComparisonMode.Single &&
    viewMode === ViewMode.InitiativeOverview &&
    (activeTab === Tab.Portfolios || activeTab === Tab.Teams);

  const handleInitiativeOverviewCSVExport = () => {
    if (!initiatives || initiatives.length === 0) return;
    if (selectedInitiativeStatuses.length === 0) return;
    if (tableData.length === 0) return;

    exportInitiativeOverviewCSV(tableData, t);
  };

  return shouldShowInitiativeOverviewCSVExport ? handleInitiativeOverviewCSVExport : undefined;
};

/**
 * Hook that returns appropriate CSV export handler based on current view mode
 * This is the main entry point for CSV export functionality
 *
 * @returns {() => void | undefined} CSV export handler function or undefined if CSV export is not available
 */
export const useCSVExportHandler = (): (() => void) | undefined => {
  const comparisonMode = useComparisonMode();
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);

  const monthlyAveragesCSVExport = useMonthlyAveragesCSVExport();
  const capacityCSVExport = useCapacityCSVExport();
  const initiativeOverviewCSVExport = useInitiativeOverviewCSVExport();

  if (comparisonMode === ComparisonMode.Single) {
    if (viewMode === ViewMode.Capacity && activeTab === Tab.Portfolios) {
      return capacityCSVExport;
    }
    if (viewMode === ViewMode.InitiativeOverview && (activeTab === Tab.Portfolios || activeTab === Tab.Teams)) {
      return initiativeOverviewCSVExport;
    }
  }

  if (comparisonMode === ComparisonMode.Multiple) {
    if (multipleViewMode === MultipleViewMode.MonthlyAverages) {
      return monthlyAveragesCSVExport;
    }
    if (multipleViewMode === MultipleViewMode.Capacity && activeTab === Tab.Teams) {
      return capacityCSVExport;
    }
  }

  return undefined;
};
